/**
 * AccountHierarchyLinks — the "Hierarchy" block in the account drilldown's left
 * field panel (both shells): the Parent account and the Child accounts as links.
 * Reads the Hierarchy tab's own cached query (`useAccountHierarchy`, same key) and
 * resolves parent/children with the shared `hierarchyNeighbors`, so the sidebar and
 * the tab can never disagree. Quiet by design: nothing renders while loading, on
 * error, or for an account with no hierarchy.
 *
 * `variant` only picks the row styling to match each shell's own field panel
 * (V1 `DetailKV` rows, V2 `EntityFields` column rows); the data and structure are shared.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { hierarchyNeighbors } from '@shared/flow/account-hierarchy-tree';
import { Icon } from '@clay-run/glaze/icon';
import { FLOW_ICONS } from '../../icons';
import { useAccountHierarchy } from '../hooks';
import { HIERARCHY_COPY, SEPARATE_BUYING_CENTER_LABEL, buyingCenterText } from './hierarchyPresentation';

/** Child links shown before "+N more" (which opens the Hierarchy tab). */
export const HIERARCHY_LINKS_CHILD_CAP = 8;

export interface AccountHierarchyLinksProps {
  accountId: string;
  /** Builds a company's drilldown route — the shell owns the URL shape. */
  accountHref: (salesforceAccountId: string) => string;
  /** "+N more": switch the drilldown to its Hierarchy tab. */
  onOpenHierarchyTab: () => void;
  variant: 'v1' | 'v2';
}

const LINK_CLASS = 'text-content-action hover:underline';

function Row({ variant, label, testId, children }: { variant: 'v1' | 'v2'; label: string; testId: string; children: ReactNode }) {
  if (variant === 'v1') {
    return (
      <div data-testid={testId} className="flex py-[3px] border-b border-primary/40 text-[12px] last:border-b-0">
        <span className="w-[104px] shrink-0 text-content-tertiary">{label}</span>
        <span className="flex min-w-0 flex-col font-medium text-content-primary">{children}</span>
      </div>
    );
  }
  return (
    <div data-testid={testId} className="flex items-start justify-between gap-3 border-b-[0.5px] border-border-primary py-1.5 last:border-b-0">
      <span className="min-w-0 text-xs text-content-tertiary">{label}</span>
      <span className="flex min-w-0 flex-col items-end text-sm">{children}</span>
    </div>
  );
}

export function AccountHierarchyLinks({ accountId, accountHref, onOpenHierarchyTab, variant }: AccountHierarchyLinksProps) {
  const { data } = useAccountHierarchy(accountId);
  if (!data) return null;
  const neighbors = hierarchyNeighbors(data.nodes, data.root_id, data.current_id);
  const viewed = data.nodes.find(n => n.id === data.current_id);
  const buyingCenter = viewed?.separate_buying_center ? viewed : null;
  // A lone account has no hierarchy to link, but a flagged one still gets the buying-center row.
  if (!neighbors && !buyingCenter) return null;

  const { parent, parentMissing, children } = neighbors ?? { parent: null, parentMissing: false, children: [] };
  const shown = children.slice(0, HIERARCHY_LINKS_CHILD_CAP);
  const hidden = children.length - shown.length;
  const none = <span className="text-content-tertiary">None</span>;

  return (
    <div data-testid="account-hierarchy-links" className={clsx(variant === 'v1' && 'break-inside-avoid mb-4')}>
      {variant === 'v1' ? (
        <div className="mb-1 text-[10.5px] font-medium uppercase tracking-wide text-content-tertiary">Hierarchy</div>
      ) : (
        <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-content-tertiary">Hierarchy</span>
      )}
      {neighbors && (<>
      <Row variant={variant} label="Parent account" testId="account-hierarchy-parent">
        {parent ? (
          <Link to={accountHref(parent.id)} className={LINK_CLASS}>{parent.name ?? parent.id}</Link>
        ) : parentMissing ? (
          <span className="text-content-tertiary">{HIERARCHY_COPY.parentMissing}</span>
        ) : none}
      </Row>
      <Row variant={variant} label="Child accounts" testId="account-hierarchy-children">
        {shown.length === 0 ? (data.truncated ? <span className="text-content-tertiary">{HIERARCHY_COPY.childrenIncomplete}</span> : none) : shown.map(c => (
          <Link key={c.id} to={accountHref(c.id)} className={LINK_CLASS}>{c.name ?? c.id}</Link>
        ))}
        {hidden > 0 && (
          <button type="button" onClick={onOpenHierarchyTab} className={clsx(LINK_CLASS, 'text-left')}>
            {`+${hidden} more`}
          </button>
        )}
        {data.truncated && hidden === 0 && (
          <button type="button" onClick={onOpenHierarchyTab} className={clsx(LINK_CLASS, 'text-left')}>
            {HIERARCHY_COPY.seeMoreInTab}
          </button>
        )}
      </Row>
      </>)}
      {buyingCenter && (
        <div
          data-testid="account-hierarchy-buying-center"
          className={clsx('flex flex-col gap-0.5 py-1.5', variant === 'v1' ? 'text-[12px]' : 'text-sm')}
        >
          <span className={clsx('flex items-center gap-1 font-medium text-content-primary', variant === 'v2' && 'text-xs')}>
            <Icon type={FLOW_ICONS.separateBuyingCenter} weight="fill" size="xs" aria-hidden className="text-content-action" />
            {SEPARATE_BUYING_CENTER_LABEL}
          </span>
          <span className="text-content-secondary">{buyingCenterText(buyingCenter)}</span>
        </div>
      )}
    </div>
  );
}
